/**
 * This custom hook provides the functionality to retrieve users to be shown in the UI.
 */

import {useContext, useEffect, useState} from "react";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";
import moment from "moment";
import {useSnackbar} from "notistack";
import {useIntl} from "react-intl";

const useFetchSelectableFilterItems = () => {
    const [selectionBasis, setSelectionBasis] = useState()

    const {signedInUser} = useContext(FirebaseAuthContext);
    const {enqueueSnackbar} = useSnackbar();

    // Internationalization for simple strings
    const intl = useIntl();

    /*
     Get all data, which is necessary to conditionally render the selectable existingOptions for the statistical filtering.
     This function will be loaded only once after the component entered - this is done to improve performance.
      */
    const fetchSelectionBasisData = async () => {
        const idToken = await signedInUser.getIdToken()
        return fetch('/reg-main/statistics/admin/filter/selectable?idToken=' + idToken)
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }

                return data.json();
            })
            .catch((error) => {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.statistics.messages.load.selectableFilterEntries.error',
                        defaultMessage: 'Something went wrong while loading the selectable filter entries: {error}'
                    }, {error: error.message}), {
                        variant: 'error',
                        preventDuplicate: true,
                        autoHideDuration: 5000
                    });
            })
    }

    useEffect(() => {
        fetchSelectionBasisData()
            .then((basisData) => {
                setSelectionBasis(basisData)
            })
    }, [])

    /*
     Separate function to provide the basic data by entity - this approach has been chosen, because
     the filtering of the available elements can be done more elegant with a then-chaining.
     */
    const getAllLocalAssociations = async () => {
        return selectionBasis.associations
    }

    const getAllLocalDisciplines = async () => {
        return selectionBasis.disciplines
    }

    const getAllLocalCourses = async () => {
        return selectionBasis.courses
    }

    const getAllLocalPlaylists = async () => {
        return selectionBasis.playlists
    }

    const getAllLocalUsers = async () => {
        return selectionBasis.users
    }

    const getAllLocalDifficulties = async () => {
        return selectionBasis.difficulties
    }

    /*
     Get selectable associations (no dependencies are existing here - hence also no restricting of the dataset is necessary)
     */
    const getSelectableAssociations = async () => {
        return getAllLocalAssociations()
    }

    /*
     Get selectable difficulties (no dependencies are existing here - hence also no restricting of the dataset is necessary)
     */
    const getSelectableDifficulties = async () => {
        return getAllLocalDifficulties()
    }

    /*
     Get selectable disciplines based on the selected associations.
     */
    const getSelectableDisciplines = async (associationsFilter) => {
        return getAllLocalDisciplines()
            .then((disciplineStructure) => {

                // Filter by associations
                if (associationsFilter.length > 0) {
                    const list = []
                    associationsFilter.forEach((association) => {
                        list.push(...disciplineStructure[association])
                    })
                    return list
                }

                // No associations filter active
                const allDisciplines = []
                Object.keys(disciplineStructure).forEach((association) => {
                    allDisciplines.push(...disciplineStructure[association])
                })
                return allDisciplines
            })
    }

    /*
     Get selectable courses based on the selected associations and disciplines.
     */
    const getSelectableCourses = async (associationsFilter, disciplinesFilter) => {
        return getAllLocalCourses()
            .then((courses) => {
                // Filter by associations
                if (associationsFilter.length > 0) {
                    return courses.filter((course) => associationsFilter.includes(course.association))
                }

                // No associations filter active
                return courses
            })
            .then((courses) => {

                // Filter by disciplines
                if (disciplinesFilter.length > 0) {
                    return courses.filter((course) => {
                        return course.disciplines.find((discipline) => disciplinesFilter.includes(discipline))
                    })
                }

                // No disciplines filter active
                return courses
            })
    }

    /*
     Get selectable playlists based on the selected associations, disciplines and courses.
     IMPORTANT: Only playlists are considered, which are in a course (for playlists which are not in a course, there won't be a statistic anyway)
     */
    const getSelectablePlaylists = async (associationsFilter, disciplinesFilter, coursesFilter) => {
        const allPlaylists = await getAllLocalPlaylists()

        return getSelectableCourses(associationsFilter, disciplinesFilter)
            .then((courses) => {

                // Filter by courses
                if (coursesFilter.length > 0) {
                    return courses.filter((course) => coursesFilter.includes(course.key))
                }

                // No courses filter active
                return courses
            })
            .then((courses) => {

                // Collect all playlists available in the course
                const playlistKeys = []
                courses.forEach((course) => {
                    playlistKeys.push(...course.playlists)
                })
                return playlistKeys
            })
            .then((playlistKeys) => {

                // Remove the duplicates from the collected playlists
                const duplicateFreeList = []
                playlistKeys.forEach((playlistKey) => {
                    if (!duplicateFreeList.includes(playlistKey)) {
                        duplicateFreeList.push(playlistKey)
                    }
                })
                return duplicateFreeList
            })
            .then((playlistKeys) => {
                return allPlaylists.filter((playlist) => playlistKeys.includes(playlist.key))
            })
    }

    /*
     Get selectable users based on the selected associations, disciplines and courses.

     Special case: For performance statistic, the selectable users are drilled-down based on the selectable courses
     in the hierarchical selection. However, for object creation statistics this does not make sense, as these are not
     related to courses. Therefore, for object creation statistics, we provide all users authorized for the signed-in user's.
     */
    const getSelectableUsers = async (associationsFilter, disciplinesFilter, coursesFilter, metric) => {
        const allUsers = await getAllLocalUsers()

        if (['createdScoredScenes', 'createdUsers'].includes(metric)) {
            return allUsers
        } else {
            return getSelectableCourses(associationsFilter, disciplinesFilter)
                .then((courses) => {

                    // Filter by courses
                    if (coursesFilter.length > 0) {
                        return courses.filter((course) => coursesFilter.includes(course.key))
                    }

                    // No courses filter active
                    return courses
                })
                .then((courses) => {

                    // Collect all playlists available in the course
                    const participantUids = []
                    courses.forEach((course) => {
                        participantUids.push(...course.participants)
                    })
                    return participantUids
                })
                .then((participantUids) => {

                    // Remove the duplicates from the collected users
                    const duplicateFreeList = []
                    participantUids.forEach((participantUid) => {
                        if (!duplicateFreeList.includes(participantUid)) {
                            duplicateFreeList.push(participantUid)
                        }
                    })
                    return duplicateFreeList
                })
                .then((userKeys) => {
                    return allUsers.filter((user) => userKeys.includes(user.uid))
                })
        }
    }

    /*
     Get selectable users expertises based on the selected associations, disciplines, courses and users.

     Special case: For performance statistic, the selectable expertises are drilled-down based on the selectable courses users'
     in the hierarchical selection. However, for object creation statistics this does not make sense, as these are not
     related to courses. Therefore, for object creation statistics, we provide all users' expertises authorized for the signed-in user's.
     */
    const getSelectableExpertises = async (associationsFilter, disciplinesFilter, coursesFilter, usersFilter, metric) => {
        return getSelectableUsers(associationsFilter, disciplinesFilter, coursesFilter, metric)
            .then((users) => {
                // Filter by users
                if (usersFilter.length > 0) {
                    return users.filter((user) => usersFilter.includes(user.uid))
                }

                // No users filter active
                return users
            })
            .then((users) => {
                const list = []
                users.forEach((u) => {
                    if (u.associationInfo) {
                        Object.keys(u.associationInfo).forEach((a) => {
                            if (associationsFilter.includes(a) || associationsFilter.length === 0) {
                                list.push(u.associationInfo[a].experience)
                            }
                        })
                    }
                })
                return list
            })
            .then((expertises) => {

                // Remove the duplicates from the collected expertises
                const duplicateFreeList = []
                expertises.forEach((expertise) => {
                    if (!duplicateFreeList.includes(expertise)) {
                        duplicateFreeList.push(expertise)
                    }
                })
                return duplicateFreeList
            })
    }

    /*
     Functions to transform the objects returned from the above functions into proper {id, value} pairs to
     be shown as selectable entry in the multi-select.
     */

    const getSelectableAssociationItems = async () => {
        return getSelectableAssociations()
            .then((associations) => {
                return associations.map((association) => {
                    return {
                        id: association,
                        name: association
                    }
                })
            })
    }

    const getSelectableDifficultyItems = async () => {
        return getSelectableDifficulties()
            .then((difficulties) => {
                return difficulties.map((difficulty) => {
                    return {
                        id: difficulty,
                        name: difficulty
                    }
                })
            })
    }

    const getSelectableDisciplineItems = async (associationsFilter) => {
        return getSelectableDisciplines(associationsFilter)
            .then((disciplines) => {
                return disciplines.map((discipline) => {
                    return {
                        id: discipline,
                        name: discipline
                    }
                })
            })
    }

    const getSelectableCourseItems = async (associationsFilter, disciplinesFilter) => {
        return getSelectableCourses(associationsFilter, disciplinesFilter)
            .then((courses) => {
                return courses.map((course) => {
                    return {
                        id: course.key,
                        name: course.name,
                        groupBy: 'Q' + moment(course.fromDate).quarter() + '/' + moment(course.fromDate).year()
                    }
                })
            })
    }

    const getSelectablePlaylistItems = async (associationsFilter, disciplinesFilter, coursesFilter) => {
        return getSelectablePlaylists(associationsFilter, disciplinesFilter, coursesFilter)
            .then((playlists) => {
                return playlists.map((playlist) => {
                    return {
                        id: playlist.key,
                        name: playlist.name,
                        groupBy: playlist.name.toUpperCase().charAt(0)
                    }
                })
            })
    }

    const getSelectableUserItems = async (associationsFilter, disciplinesFilter, coursesFilter, metric) => {
        return getSelectableUsers(associationsFilter, disciplinesFilter, coursesFilter, metric)
            .then((users) => {
                return users.map((user) => {
                    return {
                        id: user.uid,
                        name: user.firstName + ' ' + user.secondName,
                        groupBy: user.secondName.toUpperCase().charAt(0)
                    }
                })
            })
    }

    const getSelectableExpertiseItems = async (associationsFilter, disciplinesFilter, coursesFilter, usersFilter, metric) => {
        return getSelectableExpertises(associationsFilter, disciplinesFilter, coursesFilter, usersFilter, metric)
            .then((expertises) => {
                return expertises.map((expertise) => {
                    return {
                        id: expertise,
                        name: expertise
                    }
                })
            })
    }

    return {
        selectionBasis,
        getSelectableAssociationItems,
        getSelectableDifficultyItems,
        getSelectableDisciplineItems,
        getSelectableCourseItems,
        getSelectablePlaylistItems,
        getSelectableUserItems,
        getSelectableExpertiseItems
    }
}
export default useFetchSelectableFilterItems;